import { hashSeed } from './mapPlacement'
import type { AllocationBoard } from './types'

/**
 * Default column count for `PhoneLineGrid` when no `cols` is passed. Retained from
 * the retired standalone board; the dashboard passes ALLOCATION_GRID_COLS instead.
 */
export const GRID_COLS = 24

/**
 * Column count for the line grid on the combined dashboard. Divides the 600-line
 * pool evenly (40 × 15) so every row is full — no partial last row. The grid card
 * is authoritative for the top row's height; the map beside it scales its image to
 * match that height rather than the taller card winning (see PhoneLineBoardPage).
 */
export const ALLOCATION_GRID_COLS = 40

/**
 * The pool split the whole page renders from: `held` is `Σ reserved_by_duty` and
 * `free` is the leftover, floored at zero.
 *
 * Deliberately NOT read from `pool.reserved` / `pool.free`. Every reserved line is
 * attributed to a duty, so the segment sums reconcile with the pool totals — but
 * only the segment sums are guaranteed to add up to `total`, which is what the
 * ribbon needs to fill exactly 100%. Deriving the header from the same helper
 * keeps the two from disagreeing if the backend ever drifts (the header would
 * otherwise say "330 free" while the segment right below it said "234 free").
 */
export function poolSplit(
  board: Pick<AllocationBoard, 'pool' | 'reserved_by_duty'>
): {
  total: number
  held: number
  free: number
} {
  const total = board.pool.total
  const held = board.reserved_by_duty.reduce((sum, d) => sum + d.reserved, 0)
  return { total, held, free: Math.max(0, total - held) }
}

/**
 * The colour of palette slot `index`. Hues step by the golden angle (~137.5°) so
 * consecutive slots — which can render adjacent in the ribbon — land far apart on
 * the wheel. Fixed saturation/lightness (mid-dark) read on both light and dark
 * surfaces and keep white segment labels legible.
 *
 * The palette wraps at `DUTY_PALETTE_SIZE`, so two duties can share a slot, and
 * past ~10–12 distinct slots some hues get perceptually close regardless. Neither
 * matters much: colour is never the sole cue — the in-segment labels, the legend,
 * and the table all name the duty. Returned as a CSS colour string, applied via
 * `style` (a hue per duty can't be a static Tailwind class).
 */
export function dutyColor(index: number): string {
  const hue = Math.round((index * 137.508) % 360)
  return `hsl(${hue} 62% 45%)`
}

/** Fallback if a rendered duty id is somehow absent from the color map. */
export const UNKNOWN_DUTY_COLOR = 'hsl(0 0% 60%)'

/**
 * Number of hue slots a duty can land in. Duties are hashed into this palette, so
 * two duties can share a hue — with 24 slots that is likely once ~8+ duties are on
 * screen at once (birthday problem, not a bug). Accepted: the swatch is never the
 * sole identifier — the ribbon labels, the legend, and the table all name the duty.
 * Trading collisions for stability is the point; see `buildDutyColorMap`.
 */
const DUTY_PALETTE_SIZE = 24

/**
 * Stable duty → color mapping. A duty's hue is a pure function of its `duty_id`
 * (FNV-1a hash into `DUTY_PALETTE_SIZE` golden-angle slots), so it survives
 * anything that changes the *set* of duties on screen.
 *
 * This previously assigned slots by position in the sorted id list, which looked
 * stable but wasn't: when a duty left either array — routine, as backlogs drain —
 * every duty sorting after it shifted down a slot and took on a neighbour's colour.
 * A duty could silently inherit the hue another one just vacated, which is worse
 * than an arbitrary colour: it makes "the green one" mean a different duty than it
 * did a poll ago.
 *
 * Still returns a Map so callers keep one lookup per render, and still takes only
 * the two arrays it reads so they can memoize on exactly those — the board object
 * gets a new identity every poll (`generated_at`).
 */
export function buildDutyColorMap(
  board: Pick<AllocationBoard, 'reserved_by_duty' | 'duties'>
): Map<string, string> {
  const map = new Map<string, string>()
  const ids = [
    ...board.reserved_by_duty.map((d) => d.duty_id),
    ...board.duties.map((d) => d.duty_id),
  ]
  for (const id of new Set(ids)) {
    map.set(id, dutyColor(hashSeed(id) % DUTY_PALETTE_SIZE))
  }
  return map
}

/**
 * Traffic-light colours for hourly-cap utilization, from the dataviz reserved
 * status palette (fixed across light/dark, validated ≥3:1 on both surfaces —
 * good/warning/critical). Applied via inline `style` alongside the bar's numeric
 * tooltip, so colour is a redundant cue, never the sole carrier of meaning.
 */
const CAP_STATUS_GOOD = '#0ca30c' // green — comfortably under cap
const CAP_STATUS_WARNING = '#fab219' // amber — nearing cap
const CAP_STATUS_CRITICAL = '#d03b3b' // red — at/over cap

/** Utilization (percent of cap) thresholds where the ramp steps up. */
const CAP_WARNING_PCT = 75
const CAP_CRITICAL_PCT = 90

/**
 * Fill colour for the hourly-cap bar as utilization approaches the cap: green
 * well under, amber as it nears, red at/over. `utilizationPct` is
 * `calls_this_hour / hourly_cap * 100` (may exceed 100 when over cap → red).
 */
export function hourlyCapStatusColor(utilizationPct: number): string {
  if (utilizationPct >= CAP_CRITICAL_PCT) return CAP_STATUS_CRITICAL
  if (utilizationPct >= CAP_WARNING_PCT) return CAP_STATUS_WARNING
  return CAP_STATUS_GOOD
}

/**
 * Text colour for the hourly-cap label — the same green/amber/red tiers as
 * `hourlyCapStatusColor`, but darker, contrast-safe steps that stay legible as
 * small text on both surfaces (the vivid fill steps are sub-3:1 as text on the
 * light surface). Dark-mode-aware Tailwind classes, mirroring `importanceBadgeClass`.
 */
export function hourlyCapStatusTextClass(utilizationPct: number): string {
  if (utilizationPct >= CAP_CRITICAL_PCT)
    return 'text-red-700 dark:text-red-400'
  if (utilizationPct >= CAP_WARNING_PCT)
    return 'text-amber-700 dark:text-amber-400'
  return 'text-green-700 dark:text-green-400'
}

/**
 * Importance-badge styling, tiered like the visual reference (imp3 blue, imp2
 * violet, imp1/other neutral). Importance is a positive integer that degrades to
 * 1 on the backend, so anything ≥3 shares the top tier.
 */
export function importanceBadgeClass(importance: number): string {
  if (importance >= 3)
    return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
  if (importance === 2)
    return 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300'
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
}
